import {FormField} from "./FormField";
import FormValidation from "./FormValidation";
import {none, Option, some} from "ts-option";

export default abstract class BaseFormController<T extends BaseFormController<T>> {
	constructor(public readonly fieldWithFocus: FormField<any>,
		public readonly formId: string) {}

	/**
	 * Check if field has focus.
	 * @param field field to check.
	 * @return if field has focus.
	 */
	hasFocus(field: FormField<any>): boolean {
		return field.id === this.fieldWithFocus.id;
	}

	/**
	 * Set focus on the field.
	 * @param field new filed with focus.
	 * @return updated form.
	 */
	setFocus(field: FormField<any>): T {
		return this.copy({fieldWithFocus: field}) as any;
	}

	/**
	 * Validate all fields.
	 * @return updated form.
	 */
	validate(): T {
		return FormValidation.validateForm<T>(this as any, (form, config) => form.copy(config)) as any;
	}

	/**
	 * Update field of the form.
	 * @param updatedField updated field.
	 * @return updated form.
	 */
	updateField(updatedField: FormField<any>): T {
		return FormValidation.updateField<T>(this as any, updatedField, (form, config) => form.copy(config)) as any;
	}

	/**
	 * Update multiple fields of the form.
	 * @param fields updated fields.
	 * @return updated form.
	 */
	updateFields(fields: FormField<any>[]): T {
		return FormValidation.updateFields<T>(this as any, fields, (form, config) => form.copy(config)) as any;
	}

	/**
	 * Update field and set focus on next field.
	 * @param updatedField updated field.
	 * @return updated form.
	 */
	completeField(updatedField: FormField<any>): T {
		const updatedForm = FormValidation.updateField<T>(this as any, updatedField, (form, config) => form.copy(config));
		const fields: FormField<any>[] = updatedForm.fields();
		let updatedFieldIndex = null;
		for (let i = 0; i < fields.length * 2; i++) {
			const index = i % fields.length;
			if (fields[index].id === updatedField.id) {
				updatedFieldIndex = index;
				break;
			}
		}
		let optionlNextField: Option<FormField<any>> = none;
		for (let i = updatedFieldIndex + 1; i < fields.length * 2; i++) {
			const index = i % fields.length;
			if (fields[index].value.isEmpty) {
				optionlNextField = some(fields[index]);
				break;
			}
		}
		return optionlNextField.map(nextField => updatedForm.setFocus(nextField)).getOrElse(updatedForm) as any;
	}

	/**
	 * Find field by id.
	 * @param id id of field to find.
	 * @return optional field.
	 */
	findFieldById<F extends FormField<any>>(id: string): Option<F> {
		let foundField = none;
		Object.keys(this).forEach(key => {
			let field = (<any>this)[key];
			if (field.id) {
				if (field.id == id)
					foundField = some(field);
			}
		});
		return foundField;
	}

	/**
	 * Names of the fields whose value differs from the same field in an earlier state of this form, e.g. as loaded.
	 * A value typed back to what it was counts as unchanged.
	 * @param initial earlier state of the form.
	 * @return names of the changed fields.
	 */
	changedFieldKeys(initial: T): string[] {
		return this.fieldKeys().filter(key =>
			!BaseFormController.sameValue((<any>this)[key].value, (<any>initial)[key] && (<any>initial)[key].value));
	}

	/**
	 * Names of the form's fields. The focused field is a copy of one of them, so it is left out.
	 * @return names of the fields.
	 */
	fieldKeys(): string[] {
		return Object.keys(this).filter(key => {
			const field = (<any>this)[key];
			return key !== "fieldWithFocus" && !!field && typeof field.validate === "function";
		});
	}

	/**
	 * Compares two field values the way a user sees them: an empty text is no value, and "1" typed into
	 * a number field equals the number 1.
	 */
	private static sameValue(a: Option<any>, b: Option<any>): boolean {
		const normalize = (value: Option<any>) => {
			const v = value ? value.orNull : null;
			if (v === null || v === undefined || (typeof v === "string" && v.trim() === "")) {
				return null;
			}
			return typeof v === "object" ? JSON.stringify(v) : String(v);
		};
		return normalize(a) === normalize(b);
	}

	/**
	 * Copy form.
	 * @param config config for copy.
	 * @return copy of the form.
	 */
	abstract copy(config: {}): T

	/**
	 * Fields from form.
	 * @return fields.
	 */
	abstract fields(): FormField<any>[];
}